// pages/messenger/messenger-joboverview.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Stepper, Step } from 'react-form-stepper';
import RequestService from '../../services/requestService';
import { WF } from '../../components/utils/messenger-constants';
import { getTimeLabel, getStopTimeLabel, getEmail, getStopBadge, getTaskStatus } from '../../components/utils/messenger-helpers';
import LoadingDots from '../../components/common/loading-dots';
import RouteArrow from '../../components/common/route-arrow';

const MessengerJobOverview = () => {
    const navigate              = useNavigate();
    const { id: encodedId }     = useParams();
    const id                    = atob(encodedId);
    const [job, setJob]         = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    useEffect(() => {
        const fetchJob = async () => {
            setLoading(true);
            try {
                const resp = await RequestService.GetRequestById(id);
                if (resp?.data?.is_success === true) {
                    const jobData = resp.data.result;
                    if (jobData.id_workflow_status === WF.USER_CANCEL || jobData.id_workflow_status === WF.ADMIN_CANCEL) {
                        navigate(
                            `/messenger/job-cancelled/${btoa(String(jobData.id))}/${btoa(jobData.ref_no)}`,
                            { replace: true }
                        );
                        return;
                    }
                    setJob(jobData);
                } else {
                    setError('ไม่พบข้อมูลงาน');
                }
            } catch (err) {
                console.error(err);
                setError('เกิดข้อผิดพลาด');
            } finally {
                setLoading(false);
            }
        };
        fetchJob();
    }, [id]);

    const handleStart = () => {
        const firstSeq = details[0]?.seq ?? 1;
        navigate(`/messenger/job-detail/${btoa(id)}/${btoa(String(firstSeq))}`);
    };

    // complete ทุก stop ที่ยังค้างอยู่ แล้ว change workflow เป็น COMPLETE
    const handleCompleteAll = async () => {
        setLoading(true);
        try {
            const pendingDetails = details.filter(d => d.is_complete != 1);

            for (const d of pendingDetails) {
                await RequestService.PatchRequestDetail(d.id, {
                    is_complete: 1,
                    update_by: getEmail(),
                });
            }

            await RequestService.ChangeWorkflowStatus(id, {
                id_workflow_status: WF.COMPLETE,
                update_by: getEmail(),
            });

            navigate(
                `/messenger/job-done/${btoa(id)}/${btoa('0')}/${btoa(String(totalStops))}`,
                { replace: true }
            );
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingDots />;
    if (error) return (
        <div className="mjd-root">
            <div className="mjd-header">
                <button className="mjd-back-btn" onClick={() => navigate(-1)}>
                    <i className="mdi mdi-arrow-left" />
                </button>
                <h1 className="mjd-header-title">รายละเอียดงาน</h1>
            </div>
            <div className="mjd-error">{error}</div>
        </div>
    );

    const details    = job?.request_details || [];
    const totalStops = details.length;
    const wfStatus   = job?.id_workflow_status;
    const isDoing    = wfStatus === WF.DOING;
    const isAssign   = wfStatus === WF.ASSIGN || wfStatus === WF.REASSIGN || wfStatus === WF.CREATE;
    // ── "ส่งแล้วทั้งหมด" อิงตาม is_complete=1 (ส่งสำเร็จจริง) ──
    const doneCount  = details.filter(d => d.is_complete == 1).length;

    const sortedDetails = [...details].sort((a, b) => {
        if (a.is_complete == 1 && b.is_complete != 1) return 1;
        if (a.is_complete != 1 && b.is_complete == 1) return -1;
        return (a.seq ?? 0) - (b.seq ?? 0);
    });

    // ── ใช้ getTaskStatus(detail) อ่านจาก send_start_date/send_end_date/is_complete แทน sessionStorage ──
    const activeIndex = isDoing
        ? details.some(d => getTaskStatus(d) === 'inprogress')
            ? doneCount
            : -1
        : -1;

    return (
        <div className="mjd-root">
            <div className="mjd-header">
                <button className="mjd-back-btn" onClick={() => navigate(-1)}>
                    <i className="mdi mdi-arrow-left" />
                </button>
                <h1 className="mjd-header-title">รายละเอียดงาน</h1>
            </div>

            <div className="mjd-content">
                <div className="mjo-overview-header">
                    <p className="mjo-overview-title">สถานะการจัดส่งทั้งหมด</p>
                    <p className="mjo-overview-sub">ส่งแล้วทั้งหมด {doneCount}/{totalStops}</p>
                </div>

                <Stepper
                    activeStep={-1}
                    styleConfig={{
                        activeBgColor:      '#e2e8f0',
                        activeTextColor:    'transparent',
                        completedBgColor:   '#22C55E',
                        completedTextColor: '#fff',
                        inactiveBgColor:    '#e2e8f0',
                        inactiveTextColor:  'transparent',
                        size:               '2.5em',
                        circleFontSize:     '0',
                        borderRadius:       '50%',
                        fontWeight:         '600',
                    }}
                    connectorStateColors={true}
                    connectorStyleConfig={{
                        completedColor: '#22C55E',
                        activeColor:    '#e2e8f0',
                        disabledColor:  '#e2e8f0',
                        size: 2,
                    }}
                >
                    {details.map((_, i) => (
                        <Step
                            key={i}
                            completed={i < doneCount}
                            label=""
                            className={activeIndex === i ? 'step-active' : ''}
                        />
                    ))}
                </Stepper>

                <div className="mjo-list">
                    {sortedDetails.map((detail, i) => {
                        const seq = detail.seq ?? (i + 1);

                        // ── ใช้ getStopBadge(detail) — อิงจาก send_start_date/send_end_date/is_complete ──
                        // รอจัดส่ง / กำลังส่ง / ส่งแล้ว / จบงาน
                        const badge = getStopBadge(detail);
                        const isStarted = getTaskStatus(detail) === 'inprogress';

                        return (
                            <div
                                key={detail.id}
                                className={`mjo-card ${isStarted ? 'mjo-card--inprogress' : ''}`}
                                onClick={() => navigate(`/messenger/job-detail/${btoa(id)}/${btoa(String(seq))}`)}
                            >
                                <div className="mjo-card-header">
                                    <div className="mjo-seq-badge">{seq}</div>
                                    <span className="mjo-ref">Ref No. {job?.ref_no}{totalStops > 1 ? `-${seq}` : ''}</span>
                                    <span className={`mmj-badge mmj-badge--${badge.type}`}>
                                        {badge.label}
                                    </span>
                                </div>

                                <div className="mjo-route">
                                    <div className="mjo-route-item">
                                        <span className="mjd-label">ต้นทาง</span>
                                        <span className="mjd-value">{detail.origin_building_name || '-'}</span>
                                    </div>
                                    <RouteArrow />
                                    <div className="mjo-route-item">
                                        <span className="mjd-label">ปลายทาง</span>
                                        <span className="mjd-value">{detail.destination_name || detail.destination_company || '-'}</span>
                                    </div>
                                </div>

                                <div className="mjo-card-footer">
                                    <div>
                                        <span className="mjd-label">เวลาเสร็จงาน</span>
                                        <span className="mjo-time">{getStopTimeLabel(detail, job).label}</span>
                                    </div>
                                    <i className="mdi mdi-chevron-right" style={{ color: '#94A3B8' }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* {isAssign && (
                <div className="mjd-footer">
                    <button className="mjd-btn mjd-btn--primary" onClick={handleStart}>
                        เริ่มส่งงานทั้งหมด
                    </button>
                </div>
            )} */}

            {/* {isDoing && (
                <div className="mjd-footer">
                    <button
                        className="mjd-btn mjd-btn--success"
                        // onClick={() => navigate(`/messenger/job-complete-multi/${btoa(id)}`)}
                        // ↑ job-complete-multi ยังไม่พร้อม (ยังไม่ได้คุย user เรื่อง flow จบงานทั้งหมดพร้อมกัน)
                        onClick={handleCompleteAll}
                    >
                        ส่งสำเร็จ
                    </button>
                    <button
                        className="mjd-btn mjd-btn--outline"
                        onClick={() => navigate(
                            `/messenger/job-incomplete/${btoa(id)}/${btoa('null')}/${btoa(String(totalStops))}`
                        )}
                    >
                        ขอจบงาน
                    </button>
                </div>
            )} */}
        </div>
    );
};

export default MessengerJobOverview;